import { RadioGroup } from '@components'
import React from 'react'
import { Control, Controller, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { TabButton, TabGroup } from '../segmentedTabs.styles'

type AttachmentSectionValues = {
  attachment_exempt: boolean
  copy_attachments: boolean
}

interface Props {
  nodeId: string
  canUseIncoming: boolean
  control: Control<AttachmentSectionValues>
  setValue: UseFormSetValue<AttachmentSectionValues>
  copyAttachments: boolean
  onCopyAttachmentsChange: (copy: boolean) => void
}

export const CreateDocumentAttachmentsSection: React.FC<Props> = ({
  nodeId,
  canUseIncoming,
  control,
  setValue,
  copyAttachments,
  onCopyAttachmentsChange
}) => {
  const { t } = useTranslation()

  const attachmentPolicyField = (
    <Controller
      name="attachment_exempt"
      control={control}
      render={({ field }) => (
        <RadioGroup
          name={`workflow-create-document-attachment-policy-${nodeId}`}
          value={field.value ? 'exempt' : 'book'}
          onChange={value => {
            field.onChange(value === 'exempt')
          }}
          options={[
            {
              value: 'book',
              label: t('workflows.config.createDocument.requireAttachment'),
              description: t('workflows.config.createDocument.requireAttachmentDescription')
            },
            {
              value: 'exempt',
              label: t('document.attachmentExempt'),
              description: t('workflows.config.createDocument.attachmentExemptDescription')
            }
          ]}
        />
      )}
    />
  )

  if (!canUseIncoming) {
    return (
      <Section>
        <SectionTitle>{t('workflows.config.createDocument.attachments')}</SectionTitle>
        {attachmentPolicyField}
      </Section>
    )
  }

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>{t('workflows.config.createDocument.attachments')}</SectionTitle>
        <Controller
          name="copy_attachments"
          control={control}
          render={({ field }) => (
            <SourceTabGroup
              role="radiogroup"
              aria-label={t('workflows.config.createDocument.attachments')}
            >
              {(
                [
                  {
                    value: 'incoming' as const,
                    label: t('workflows.config.createDocument.fromIncoming')
                  },
                  {
                    value: 'none' as const,
                    label: t('workflows.config.createDocument.doNotCopy')
                  }
                ] as const
              ).map(option => {
                const selected = (field.value ? 'incoming' : 'none') === option.value
                return (
                  <SourceTab
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    $selected={selected}
                    onClick={() => {
                      if (selected) return
                      const copy = option.value === 'incoming'
                      field.onChange(copy)
                      onCopyAttachmentsChange(copy)
                      if (copy) {
                        setValue('attachment_exempt', false, { shouldDirty: true })
                      }
                    }}
                  >
                    {option.label}
                  </SourceTab>
                )
              })}
            </SourceTabGroup>
          )}
        />
      </SectionHeader>
      {copyAttachments ? (
        <Hint>{t('workflows.config.createDocument.attachmentsIncomingHint')}</Hint>
      ) : (
        attachmentPolicyField
      )}
    </Section>
  )
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  min-width: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const SectionTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
`

const SourceTabGroup = styled(TabGroup)`
  max-width: 100%;
`

const SourceTab = styled(TabButton)`
  flex: 1;
  white-space: nowrap;
  line-height: 1.25;
  text-align: center;
`

const Hint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`
